"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import api from "@/config/axiosInstance";
import { endpoints } from "@/config/endpoints";
import { setToken } from "@/utils/auth";

function splitName(fullName: string) {
  const parts = fullName.trim().split(" ");
  const firstName = parts.shift() ?? "";
  const lastName = parts.join(" ");
  return { firstName, lastName };
}

export function useLoginForm() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await api.post(endpoints.auth.login, { email, password });
      setToken(response.data.access_token);
      setMessage("Login successful. Redirecting...");
      router.push("/");
    } catch {
      setMessage("Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return { email, setEmail, password, setPassword, message, loading, handleSubmit };
}

export function useRegisterForm() {
  const router = useRouter();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);

    const { firstName, lastName } = splitName(fullName);

    try {
      await api.post(endpoints.auth.register, {
        first_name: firstName,
        last_name: lastName,
        email,
        phone: phone || null,
        password,
      });
      setMessage("Account created. Redirecting to sign in...");
      router.push("/auth/login");
    } catch {
      setMessage("Unable to register. Try a different email.");
    } finally {
      setLoading(false);
    }
  };

  return {
    fullName,
    setFullName,
    email,
    setEmail,
    phone,
    setPhone,
    password,
    setPassword,
    message,
    loading,
    handleSubmit,
  };
}

export function useForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      await api.post(endpoints.auth.forgot, { email });
      setMessage("If your account exists, we sent reset instructions.");
    } catch {
      setMessage("Unable to send reset email. Try again soon.");
    } finally {
      setLoading(false);
    }
  };

  return { email, setEmail, message, loading, handleSubmit };
}

export function useResetPasswordForm() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const initialToken = searchParams.get("token") ?? "";

  const [token, setTokenState] = useState(initialToken);
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      await api.post(endpoints.auth.reset, { token, new_password: password });
      setMessage("Password updated. Redirecting to sign in...");
      router.push("/auth/login");
    } catch {
      setMessage("Unable to reset password. Check your token.");
    } finally {
      setLoading(false);
    }
  };

  return {
    token,
    setToken: setTokenState,
    password,
    setPassword,
    message,
    loading,
    handleSubmit,
  };
}
